import { useBalance } from "./BalanceProvider";
import { useScreenTransport } from "./TransportProvider";
import { useBrowserOnline } from "./useNetworkStatus";

/**
 * **Is the balance stale? — one answer, every surface that renders it.**
 *
 * The coin chip has dimmed on this expression since S4-8 (S3 §20.4 + Ivy
 * §14.3's second dim). The profile screen's balance plate has to dim on **the
 * same** one, and that is what this hook exists to guarantee:
 *
 * > **Ivy, profile spec §9.2 — RULING:** *"the stale dim applies to the surfaces
 * > that claim liveness, and to nothing else. On this screen that is exactly the
 * > coin plate (and the top-bar chip, which is chrome and already governed by
 * > §20.4). Stats, history, identity and the account block **do not dim**."*
 *
 * The rule generalises as *dim what claims to be live* — so the predicate has to
 * be shared, not re-derived. Two copies of it is how one wallet ends up rendered
 * bright in the top bar and dim 300 px below it during the same outage, which is
 * the mirror of the two-numbers defect §5.1 makes unrepresentable.
 *
 * The three facts, unchanged from `AppShell`'s original expression:
 *
 *  1. **`screen.stale`** — the mounted screen's own socket has stopped
 *     delivering (S2 §20.1 fact 1). A screen with no socket reports nothing and
 *     contributes nothing; the profile screen is one of those, which is why the
 *     other two matter here.
 *  2. **the device is offline** — an outage on every screen, socket or no
 *     socket, because nothing can refresh the number.
 *  3. **`reconcileDim`** — Ivy §14.3: after a settlement, brightness is asserted
 *     by an authoritative read, and a read that is slow or failed leaves the
 *     number unverified. Independent of the other two.
 *
 * **Dimming is never disabling** and it never substitutes `—` for the value
 * (§20.4's guardrails): it holds the last number it had.
 */
export function useBalanceDim(): boolean {
  const screen = useScreenTransport();
  const { reconcileDim } = useBalance();
  const browserOnline = useBrowserOnline();

  return screen.stale || !browserOnline || reconcileDim;
}
